// index.ts

const API_URL = process.env.API_BASE_URL;
const ENV = process.env.TOKEN_ENVIRONMENT; // "sandbox" or "production"

export default class Account {
  private clientId: string;

  constructor({ clientId }: { clientId: string; }) {
    if (!clientId) throw new Error("Client ID is required");
    this.clientId = clientId;
  }

  /**
   * Initiates a payment by creating it on your backend,
   * fetching the hosted‐pages URL, and redirecting the browser.
   */
  async initiatePayment({
    amount,
    referenceNumber,
  }: {
    amount: number;
    referenceNumber: string;
  }): Promise<void> {
    try {
      const paymentId = await this.#createPayment({ amount, referenceNumber });
      const { tokenRequestUrl } = await this.#getPaymentToken(paymentId);

      // Redirect the user to Token.io’s hosted‐pages SCA flow
      window.location.assign(tokenRequestUrl);
    } catch (error: any) {
      console.error("Error initiating payment:", error);
      throw error;
    }
  }

  /**
   * Initiates a payment for a registered user by looking up their email.
   */
  async initiateUserPaymentByEmail({
    amount,
    requestId,
    callbackURL,
    authToken,
  }: {
    amount: number;
    requestId: string;
    callbackURL: string;
    authToken?: string;
  }): Promise<void> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      const response = await fetch(
        `${API_URL}/sdk/users/${this.clientId}/payment-request`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ amount, requestId, callbackURL }),
        }
      );

      if (!response.ok) {
        throw new Error(`User payment failed: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.redirectTo) {
        window.location.assign(data.redirectTo);
      }
    } catch (error) {
      console.error("Error initiating user payment:", error);
      throw error;
    }
  }

  /**
   * Create a payment on your backend.
   */
  async #createPayment({
    amount,
    referenceNumber,
  }: {
    amount: number;
    referenceNumber: string;
  }): Promise<string> {
    const response = await fetch(
      `${API_URL}/sdk/payment/${this.clientId}?amount=${amount}&referenceNumber=${referenceNumber}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Create payment failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.paymentId;
  }

  /**
   * Fetch the one-time hosted-pages link token (URL) from your backend.
   */
  async #getPaymentToken(paymentId: string): Promise<{ tokenRequestUrl: string }> {
    const response = await fetch(
      `${API_URL}/sdk/link-token/${this.clientId}?paymentId=${paymentId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Fetch link token failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return {
      tokenRequestUrl: data.tokenRequestUrl,
    };
  }
}
